import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

interface ContactPopupProps {
  show: boolean;
  onClose: () => void;
}

const ContactPopup: React.FC<ContactPopupProps> = ({ show, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    interest: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [show]);

  const validateForm = () => {
    const { name, email, phone, message } = formData;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\d{10}$/;

    if (!name || !email || !phone || !message) {
      toast.error("All fields except plot type are required.");
      return false;
    }

    if (name.length > 100) {
      toast.error("Name must be under 100 characters.");
      return false;
    }

    if (email.length > 50 || !emailRegex.test(email)) {
      toast.error("Please enter a valid email (max 50 characters).");
      return false;
    }

    if (!phoneRegex.test(phone)) {
      toast.error("Phone number must be exactly 10 digits.");
      return false;
    }

    return true;
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    let processedValue = value;

    if (["name", "email", "phone"].includes(name)) {
      processedValue = value.replace(/^\s+/, "");
      processedValue = processedValue.replace(/\s{2,}/g, " ");
    }

    setFormData({ ...formData, [name]: processedValue });
  };

  const handleBlur = (
    e: React.FocusEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    let processedValue = value;

    if (["name", "email", "phone"].includes(name)) {
      processedValue = value.trim();
      setFormData({ ...formData, [name]: processedValue });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    const payload = {
      gt_name: formData.name,
      gt_email: formData.email,
      gt_mobile: formData.phone,
      gt_plottype: formData.interest || null,
      gt_msg: formData.message,
    };

    setIsSubmitting(true);

    try {
      const res = await axios.post(`${API_BASE}/api/getintouch/add`, payload);
      if (res.data.success) {
        toast.success("Thanks for getting in touch. We will contact you soon!");
        setFormData({
          name: "",
          email: "",
          phone: "",
          interest: "",
          message: "",
        });
        onClose();
      } else {
        toast.error(res.data.message || "Failed to send message.");
      }
    } catch (err) {
      toast.error("An error occurred while sending the message.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <style>{`
        .contact-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.7);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 99999;
          padding: 20px;
          animation: fadeIn 0.3s ease-in-out;
        }

        .contact-modal-content {
          background: white;
          border-radius: 12px;
          max-width: 600px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          position: relative;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
          animation: slideUp 0.3s ease-out;
          z-index: 100000;
        }

        .contact-modal-header {
          padding: 24px 24px 16px;
          border-bottom: 1px solid #e5e7eb;
          position: relative;
          background: white;
          z-index: 100001;
          border-radius: 12px 12px 0 0;
        }

        .contact-modal-header h3 {
            margin: 0;
            color: #1f2937;
            font-size: 1.8rem;
            font-weight: 600;
            padding-right: 40px;
        }

        .contact-modal-header p {
            margin: 0;
            color: #6b7280;
            font-size: 14px;
        }

        .contact-modal-close {
          position: absolute;
          top: 20px;
          right: 20px;
          background: #f3f4f6;
          border: none;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          font-size: 20px;
          color: #6b7280;
          z-index: 100002;
        }

        .contact-modal-close:hover {
          background: #e5e7eb;
          color: #1f2937;
        }

        .contact-modal-body {
          padding: 24px;
          position: relative;
          z-index: 100001;
        }

        .contact-form-group {
          margin-bottom: 20px;
          position: relative;
          z-index: 1;
        }

        .contact-form-group label {
              display: block;
            margin-bottom: 8px;
            color: #374151;
            font-weight: 500;
            font-size: 16px;
        }

        .contact-form-group input,
        .contact-form-group select,
        .contact-form-group textarea {
          width: 100%;
          padding: 12px 16px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          transition: all 0.2s;
          background: white;
          position: relative;
          z-index: 2;
        }

        .contact-form-group input:focus,
        .contact-form-group select:focus,
        .contact-form-group textarea:focus {
          outline: none;
          border-color: #824b76;
          box-shadow: 0 0 0 3px rgba(130, 75, 118, 0.1);
          z-index: 3;
        }

        .contact-form-group textarea {
          resize: vertical;
          min-height: 100px;
        }

        .contact-form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }

        .contact-submit-btn {
          width: 100%;
          padding: 14px 24px;
          background: #824b76;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          position: relative;
          z-index: 2;
        }

        .contact-submit-btn:hover:not(:disabled) {
          background: #6a3d60;
        }

        .contact-submit-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            transform: translateY(30px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @media (max-width: 640px) {
          .contact-form-row {
            grid-template-columns: 1fr;
          }

          .contact-modal-content {
            max-height: 95vh;
          }

          .contact-modal-header,
          .contact-modal-body {
            padding: 20px;
          }
        }
      `}</style>

      <div className="contact-modal-overlay" onClick={onClose}>
        <div
          className="contact-modal-content form"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="contact-modal-header">
            <h3>Can't Find What You're Looking For?</h3>
            <p>
              Let us help you find the perfect plot. Share your requirements!
            </p>
            <button
              className="contact-modal-close"
              onClick={onClose}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <div className="contact-modal-body">
            <form onSubmit={handleSubmit}>
              <div className="contact-form-row">
                <div className="contact-form-group">
                  <label htmlFor="popup-name">
                    Name <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <input
                    type="text"
                    id="popup-name"
                    name="name"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                  />
                </div>

                <div className="contact-form-group">
                  <label htmlFor="popup-phone">
                    Phone Number <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <input
                    type="text"
                    id="popup-phone"
                    name="phone"
                    placeholder="Your phone number"
                    value={formData.phone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                  />
                </div>
              </div>

              <div className="contact-form-row">
                <div className="contact-form-group">
                  <label htmlFor="popup-email">
                    Email <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <input
                    type="email"
                    id="popup-email"
                    name="email"
                    placeholder="Your email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                  />
                </div>

                <div className="contact-form-group">
                  <label htmlFor="popup-interest">Plot Type</label>
                  <select
                    id="popup-interest"
                    name="interest"
                    value={formData.interest}
                    onChange={handleChange}
                  >
                    <option value="">Select Plot Type</option>
                    <option value="Commercial">Commercial</option>
                    <option value="Residential">Residential</option>
                    <option value="Agricultural">Agricultural</option>
                    <option value="Industrial">Industrial</option>
                  </select>
                </div>
              </div>

              <div className="contact-form-group">
                <label htmlFor="popup-message">
                  Your Requirements <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <textarea
                  id="popup-message"
                  name="message"
                  placeholder="Tell us about your plot requirements..."
                  value={formData.message}
                  onChange={handleChange}
                  required
                />
              </div>

              <button
                type="submit"
                className="btn tf-btn bg-color-primary pd-8 w-100"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    />
                    Submitting...
                  </>
                ) : (
                  "Submit Request"
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactPopup;
